import {
  Invoice,
  ParseResponse,
  ArchiveEntry,
  EmailTemplate,
} from '@/types/invoice'

// API configuration
export const API_BASE_URL =
  (import.meta as any).env?.VITE_API_BASE_URL || '/api'
export const API_HOST = (import.meta as any).env?.VITE_API_HOST

console.log({ API_BASE_URL, API_HOST })

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// HTTP client with error handling
class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Use API_HOST for production, fallback to relative path for development
    const baseUrl = API_HOST ? `${API_HOST}${API_BASE_URL}` : API_BASE_URL
    const url = `${baseUrl}${endpoint}`

    console.log({ url })

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))

        // Handle rate limiting specifically
        if (response.status === 429) {
          console.warn('Rate limit exceeded, retrying in 1 second...')
          await new Promise((resolve) => setTimeout(resolve, 1000))
          // Retry once
          const retryResponse = await fetch(url, config)
          if (!retryResponse.ok) {
            throw new ApiError(
              'Rate limit exceeded. Please wait a moment and try again.',
              retryResponse.status,
              errorData
            )
          }
          return await retryResponse.json()
        }

        throw new ApiError(
          errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData
        )
      }

      return await response.json()
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        0
      )
    }
  }

  // Parse invoice file
  async parseFile(file: File): Promise<ParseResponse> {
    const formData = new FormData()
    formData.append('file', file)

    return this.request<ParseResponse>('/parse', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    })
  }

  // Parse plain text
  async parseText(text: string): Promise<ParseResponse> {
    return this.request<ParseResponse>('/parse-text', {
      method: 'POST',
      body: JSON.stringify({ text }),
    })
  }

  // Invoice operations
  async saveInvoices(
    invoices: Invoice[]
  ): Promise<{ success: boolean; invoices?: Invoice[]; error?: string }> {
    return this.request<{
      success: boolean
      invoices?: Invoice[]
      error?: string
    }>('/invoices', {
      method: 'POST',
      body: JSON.stringify({ invoices }),
    })
  }

  async getInvoices(): Promise<{
    success: boolean
    invoices?: Invoice[]
    error?: string
  }> {
    return this.request<{
      success: boolean
      invoices?: Invoice[]
      error?: string
    }>('/invoices')
  }

  async getInvoice(
    id: string
  ): Promise<{ success: boolean; invoice?: Invoice; error?: string }> {
    return this.request<{
      success: boolean
      invoice?: Invoice
      error?: string
    }>(`/invoices/${id}`)
  }

  async updateInvoice(
    id: string,
    updates: Partial<Invoice>
  ): Promise<{ success: boolean; invoice?: Invoice; error?: string }> {
    return this.request<{
      success: boolean
      invoice?: Invoice
      error?: string
    }>(`/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async deleteInvoice(
    id: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return this.request<{ success: boolean; message?: string; error?: string }>(
      `/invoices/${id}`,
      {
        method: 'DELETE',
      }
    )
  }

  // Archive operations
  async saveArchiveEntry(
    entry: ArchiveEntry
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return this.request<{ success: boolean; message?: string; error?: string }>(
      '/archive',
      {
        method: 'POST',
        body: JSON.stringify(entry),
      }
    )
  }

  async getArchiveEntries(): Promise<{
    success: boolean
    entries?: ArchiveEntry[]
    error?: string
  }> {
    return this.request<{
      success: boolean
      entries?: ArchiveEntry[]
      error?: string
    }>('/archive')
  }

  // Email template operations
  async getEmailTemplates(): Promise<{
    success: boolean
    templates?: EmailTemplate[]
    error?: string
  }> {
    return this.request<{
      success: boolean
      templates?: EmailTemplate[]
      error?: string
    }>('/email-templates')
  }

  async saveEmailTemplate(
    template: EmailTemplate
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return this.request<{ success: boolean; message?: string; error?: string }>(
      '/email-templates',
      {
        method: 'POST',
        body: JSON.stringify(template),
      }
    )
  }

  async deleteEmailTemplate(
    id: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return this.request<{ success: boolean; message?: string; error?: string }>(
      `/email-templates/${id}`,
      {
        method: 'DELETE',
      }
    )
  }

  // Statistics
  async getStatistics(): Promise<{
    totalInvoices: number
    overdueInvoices: number
    totalAmount: number
    overdueAmount: number
    collectionRate: number
  }> {
    return this.request<{
      totalInvoices: number
      overdueInvoices: number
      totalAmount: number
      overdueAmount: number
      collectionRate: number
    }>('/stats')
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request<{ status: string; timestamp: string }>('/health')
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Convenience functions for common operations
export const api = {
  parseFile: (file: File) => apiClient.parseFile(file),
  parseText: (text: string) => apiClient.parseText(text),
  saveInvoices: (invoices: Invoice[]) => apiClient.saveInvoices(invoices),
  getInvoices: () => apiClient.getInvoices(),
  getInvoice: (id: string) => apiClient.getInvoice(id),
  updateInvoice: (id: string, updates: Partial<Invoice>) =>
    apiClient.updateInvoice(id, updates),
  deleteInvoice: (id: string) => apiClient.deleteInvoice(id),
  saveArchiveEntry: (entry: ArchiveEntry) => apiClient.saveArchiveEntry(entry),
  getArchiveEntries: () => apiClient.getArchiveEntries(),
  getEmailTemplates: () => apiClient.getEmailTemplates(),
  saveEmailTemplate: (template: EmailTemplate) =>
    apiClient.saveEmailTemplate(template),
  deleteEmailTemplate: (id: string) => apiClient.deleteEmailTemplate(id),
  getStatistics: () => apiClient.getStatistics(),
  healthCheck: () => apiClient.healthCheck(),
}
